import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@env/environment';

@Injectable({
    providedIn: 'root',
})
export class JspRedirectService {
    private readonly jspBaseUrl = environment.JSP_BASE_URL;
    private readonly router = inject(Router);

    /**
     * Builds the JSP redirect URL for self payments
     * @param encryptedLink - Encrypted link from the API
     * @returns Complete JSP redirect URL with encrypted sMsg parameter and e parameter
     */
    buildJspRedirectUrl(encryptedLink: string): string {
        return this.buildUrl(encryptedLink);
    }

    /**
     * Builds the complete JSP URL with encoded and encrypted parameters
     * @param encryptedLink - Encrypted link from the API
     * @returns Complete JSP redirect URL with encrypted sMsg
     */
    private buildUrl(encryptedLink: string): string {
        const queryParams = new URLSearchParams({
            sMsg: encryptedLink,
            e: environment.JSP_APP_ID,
        });
        return `${this.jspBaseUrl}?${queryParams.toString()}`;
    }

    /**
     * Redirects the user to the JSP payment page
     * @param url - The JSP redirect URL
     * @param target - Target for the redirect (default: '_self')
     */
    redirectToJsp(url: string, target: '_self' | '_blank' = '_self'): void {
        if (target === '_blank') {
            globalThis.open(url, '_blank');
        } else {
            globalThis.location.href = url;
        }
    }

    /**
     * Checks if the current page is a JSP return/callback
     * @returns True if this is a JSP return page
     */
    isJspReturn(): boolean {
        return (
            globalThis.location.search.includes('sMsg') ||
            globalThis.location.search.includes('payment_status') ||
            globalThis.location.search.includes('transaction_id') ||
            globalThis.location.search.includes('reqId')
        );
    }

    /**
     * Extracts payment status from JSP return URL
     * @returns Payment status or null if not available
     */
    getPaymentStatus(): string | null {
        const urlParams = new URLSearchParams(globalThis.location.search);
        return (
            urlParams.get('payment_status') ||
            urlParams.get('status') ||
            urlParams.get('result') ||
            urlParams.get('paymentStatus')
        );
    }

    /**
     * Extracts transaction ID from JSP return URL
     * @returns Transaction ID or null if not available
     */
    getTransactionId(): string | null {
        const urlParams = new URLSearchParams(globalThis.location.search);
        return urlParams.get('transaction_id') || urlParams.get('txn_id') || urlParams.get('reqId');
    }

    /**
     * Redirects back to enrollment details with payment result parameters
     * @param enrollmentId - The enrollment ID
     * @param paymentStatus - Payment status (success/failed)
     * @param transactionId - Transaction ID if available
     */
    redirectBackToEnrollment(enrollmentId: string, paymentStatus: string, transactionId?: string): void {
        const queryParams: any = {
            paymentReturn: 'true',
            status: paymentStatus,
        };

        if (transactionId) {
            queryParams.transactionId = transactionId;
        }

        this.router.navigate(['/my-learning/enrollment-details', enrollmentId], {
            queryParams,
        });
    }
}
